// src/pages/Connector/GoogleDrive/GoogleDriveBrowser.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    Breadcrumbs,
    Link,
    Checkbox,
    CircularProgress,
    Box,
    Divider,
    IconButton,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    InputAdornment
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import CodeIcon from '@mui/icons-material/Code';
import ArchiveIcon from '@mui/icons-material/Archive';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BrushIcon from '@mui/icons-material/Brush';
import LanguageIcon from '@mui/icons-material/Language';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MapIcon from '@mui/icons-material/Map';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DataObjectIcon from '@mui/icons-material/DataObject';

const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';

// Dynamic MIME type categories for filtering
const MIME_TYPE_CATEGORIES = [
    {
        label: 'All Types',
        value: 'all',
        icon: <InsertDriveFileIcon fontSize="small" />,
        mimeTypes: []
    },
    {
        label: 'Folders',
        value: 'folders',
        icon: <FolderIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.folder'
        ]
    },
    {
        label: 'Documents',
        value: 'documents',
        icon: <DescriptionIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.document',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'application/rtf'
        ]
    },
    {
        label: 'Spreadsheets',
        value: 'spreadsheets',
        icon: <TableChartIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.spreadsheet',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
        ]
    },
    {
        label: 'Presentations',
        value: 'presentations',
        icon: <SlideshowIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.presentation',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ]
    },
    {
        label: 'Forms',
        value: 'forms',
        icon: <AssignmentIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.form'
        ]
    },
    {
        label: 'Drawings',
        value: 'drawings',
        icon: <BrushIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.drawing'
        ]
    },
    {
        label: 'Sites',
        value: 'sites',
        icon: <LanguageIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.site'
        ]
    },
    {
        label: 'Shortcuts',
        value: 'shortcuts',
        icon: <ShortcutIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.shortcut',
            'application/vnd.google-apps.drive-sdk'
        ]
    },
    {
        label: 'Vids',
        value: 'vids',
        icon: <SmartDisplayIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.vid'
        ]
    },
    {
        label: 'Gems',
        value: 'gems',
        icon: <AutoAwesomeIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.gem'
        ]
    },
    {
        label: 'Maps',
        value: 'maps',
        icon: <MapIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.map',
            'application/vnd.google-apps.fusiontable'
        ]
    },
    {
        label: 'Jamboard',
        value: 'jamboard',
        icon: <DashboardIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.jam'
        ]
    },
    {
        label: 'Scripts',
        value: 'scripts',
        icon: <CodeIcon fontSize="small" />,
        mimeTypes: [
            'application/vnd.google-apps.script'
        ]
    },
    {
        label: 'PDFs',
        value: 'pdfs',
        icon: <PictureAsPdfIcon fontSize="small" />,
        mimeTypes: ['application/pdf']
    },
    {
        label: 'Images',
        value: 'images',
        icon: <ImageIcon fontSize="small" />,
        mimeTypes: [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/svg+xml',
            'image/bmp',
            'image/tiff',
            'application/vnd.google-apps.photo'
        ]
    },
    {
        label: 'Videos',
        value: 'videos',
        icon: <VideoLibraryIcon fontSize="small" />,
        mimeTypes: [
            'video/mp4',
            'video/mpeg',
            'video/quicktime',
            'video/x-msvideo',
            'video/webm',
            'video/3gpp',
            'video/x-flv',
            'application/vnd.google-apps.video'
        ]
    },
    {
        label: 'Audio',
        value: 'audio',
        icon: <AudiotrackIcon fontSize="small" />,
        mimeTypes: [
            'audio/mpeg',
            'audio/wav',
            'audio/ogg',
            'audio/mp4',
            'audio/x-m4a',
            'audio/flac',
            'application/vnd.google-apps.audio'
        ]
    },
    {
        label: 'Code Files',
        value: 'code',
        icon: <DataObjectIcon fontSize="small" />,
        mimeTypes: [
            'text/html',
            'text/css',
            'text/javascript',
            'application/javascript',
            'application/json',
            'text/xml',
            'application/xml',
            'text/x-python',
            'application/x-python-code',
            'text/x-java-source',
            'text/x-c',
            'text/x-csharp'
        ]
    },
    {
        label: 'Archives (zip, rar, etc)',
        value: 'archives',
        icon: <ArchiveIcon fontSize="small" />,
        mimeTypes: [
            'application/zip',
            'application/x-rar-compressed',
            'application/x-7z-compressed',
            'application/gzip',
            'application/x-tar',
            'application/x-bzip2'
        ]
    }
];

export default function GoogleDriveBrowser({ isOpen, onClose, accessToken, onSelect }) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentFolder, setCurrentFolder] = useState('root');
    const [breadcrumbs, setBreadcrumbs] = useState([{ id: 'root', name: 'My Drive' }]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    
    // State for filters
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMimeCategory, setSelectedMimeCategory] = useState('all');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        if (isOpen && accessToken) {
            fetchFiles(currentFolder);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, currentFolder, accessToken]);

    // Debounced search effect
    useEffect(() => {
        if (!isOpen || !accessToken) return;
        
        const timeoutId = setTimeout(() => {
            if (searchQuery.trim() || selectedMimeCategory !== 'all') {
                performSearch();
            } else {
                fetchFiles(currentFolder);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, selectedMimeCategory]);

    const fetchFiles = async (folderId) => {
        setLoading(true);
        setError('');
        setIsSearching(false);
        try {
            const query = `'${folderId}' in parents and trashed = false`;
            const fields = 'files(id, name, mimeType, iconLink, webViewLink, thumbnailLink, modifiedTime, size)';
            const url = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=${encodeURIComponent(fields)}&pageSize=1000&orderBy=folder,name`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Google Drive API Error: ${response.statusText}`);
            }

            const data = await response.json();
            setFiles(data.files || []);
        } catch (err) {
            console.error(err);
            setError("Failed to load files from Google Drive.");
        } finally {
            setLoading(false);
        }
    };

    const performSearch = async () => {
        setLoading(true);
        setError('');
        setIsSearching(true);
        try {
            let queryParts = ['trashed = false'];
            
            // Add name search
            if (searchQuery.trim()) {
                queryParts.push(`name contains '${searchQuery.trim().replace(/'/g, "\\'")}'`);
            }
            
            // Add MIME type filter
            const category = MIME_TYPE_CATEGORIES.find(c => c.value === selectedMimeCategory);
            if (category && category.mimeTypes.length > 0) {
                const mimeConditions = category.mimeTypes.map(mt => `mimeType = '${mt}'`).join(' or ');
                queryParts.push(`(${mimeConditions})`);
            }
            
            const query = queryParts.join(' and ');
            const fields = 'files(id, name, mimeType, iconLink, webViewLink, thumbnailLink, modifiedTime, size, parents)';
            const url = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=${encodeURIComponent(fields)}&pageSize=100&orderBy=modifiedTime desc`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`Google Drive API Error: ${response.statusText}`);
            }

            const data = await response.json();
            setFiles(data.files || []);
        } catch (err) {
            console.error(err);
            setError("Failed to search files in Google Drive.");
        } finally {
            setLoading(false);
        }
    };

    const handleFolderClick = (folderId, folderName) => {
        setSearchQuery('');
        setSelectedMimeCategory('all');
        setCurrentFolder(folderId);
        setBreadcrumbs([...breadcrumbs, { id: folderId, name: folderName }]);
    };

    const handleBreadcrumbClick = (index) => {
        const target = breadcrumbs[index];
        setSearchQuery('');
        setSelectedMimeCategory('all');
        setCurrentFolder(target.id);
        setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    };

    const handleBackClick = () => {
        if (breadcrumbs.length > 1) {
            handleBreadcrumbClick(breadcrumbs.length - 2);
        }
    };

    const toggleSelection = (fileId) => {
        setSelectedFiles(prev => {
            if (prev.includes(fileId)) {
                return prev.filter(id => id !== fileId);
            } else {
                return [...prev, fileId];
            }
        });
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedMimeCategory('all');
        fetchFiles(currentFolder);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleMimeCategoryChange = (event) => {
        setSelectedMimeCategory(event.target.value);
    };

    const handleClearSearch = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setSearchQuery('');
    };

    const handleSubmit = () => {
        onSelect(selectedFiles);
        onClose();
        setSelectedFiles([]);
        setCurrentFolder('root');
        setBreadcrumbs([{ id: 'root', name: 'My Drive' }]);
        setSearchQuery('');
        setSelectedMimeCategory('all');
    };

    const handleClose = () => {
        onClose();
        setSearchQuery('');
        setSelectedMimeCategory('all');
    };

    // Get icon for file based on MIME type
    const getFileIcon = (mimeType) => {
        if (mimeType === FOLDER_MIME_TYPE) {
            return <FolderIcon sx={{ color: '#FFD04B' }} />;
        }
        
        const category = MIME_TYPE_CATEGORIES.find(c => 
            c.mimeTypes.includes(mimeType)
        );
        
        if (category) {
            return React.cloneElement(category.icon, { sx: { color: '#fff' } });
        }
        
        return <InsertDriveFileIcon sx={{ color: '#fff' }} />;
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };

    // Check if filters are active
    const hasActiveFilters = searchQuery.trim() || selectedMimeCategory !== 'all';

    return (
        <Dialog 
            open={isOpen} 
            onClose={handleClose} 
            fullWidth 
            maxWidth="md"
            sx={{ zIndex: 1350 }}
            disableEnforceFocus
            disableAutoFocus
        >
            {/* Header with Title */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 1 }}>
                <Typography variant="h6">Select Files from Google Drive</Typography>
                <IconButton onClick={handleClose}><CloseIcon /></IconButton>
            </Box>
            
            <Divider />

            {/* Navigation Bar with Search and Filter */}
            <Box sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    {/* Left side: Back button and Breadcrumbs */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
                        {breadcrumbs.length > 1 && !isSearching && (
                            <IconButton size="small" onClick={handleBackClick} sx={{ mr: 1 }}>
                                <ArrowBackIcon />
                            </IconButton>
                        )}
                        {isSearching ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SearchIcon color="primary" />
                                <Typography color="primary" fontWeight="bold">
                                    Search Results
                                </Typography>
                                {hasActiveFilters && (
                                    <Chip 
                                        label="Clear" 
                                        size="small" 
                                        onDelete={handleClearFilters}
                                        onClick={handleClearFilters}
                                        sx={{ ml: 1 }}
                                    />
                                )}
                            </Box>
                        ) : (
                            <Breadcrumbs 
                                separator={<NavigateNextIcon fontSize="small" />} 
                                aria-label="breadcrumb"
                                sx={{ minWidth: 0 }}
                            >
                                {breadcrumbs.map((crumb, index) => {
                                    const isLast = index === breadcrumbs.length - 1;
                                    return isLast ? (
                                        <Typography 
                                            key={crumb.id} 
                                            color="text.primary" 
                                            fontWeight="bold"
                                            noWrap
                                        >
                                            {crumb.name}
                                        </Typography>
                                    ) : (
                                        <Link 
                                            key={crumb.id} 
                                            component="button"
                                            variant="body1"
                                            underline="hover" 
                                            color="inherit" 
                                            onClick={() => handleBreadcrumbClick(index)}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            {crumb.name}
                                        </Link>
                                    );
                                })}
                            </Breadcrumbs>
                        )}
                    </Box>

                    {/* Right side: Search and MIME Type Filter */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5,
                            flex: '0 0 auto'
                        }}
                    >
                        {/* MIME Type Filter */}
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="mime-type-label">Types</InputLabel>
                            <Select
                                labelId="mime-type-label"
                                id="mime-type-select"
                                value={selectedMimeCategory}
                                onChange={handleMimeCategoryChange}
                                label="File Type"
                                sx={{ bgcolor: 'background.paper' }}
                                MenuProps={{
                                    disablePortal: false,
                                    sx: { zIndex: 1400 },
                                    PaperProps: {
                                        sx: { 
                                            zIndex: 1400,
                                            maxHeight: 350,
                                            overflowY: 'auto',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: 'background.default',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: 'grey.400',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                backgroundColor: 'grey.500',
                                            },
                                        }
                                    }
                                }}
                            >
                                {MIME_TYPE_CATEGORIES.map((category) => (
                                    <MenuItem key={category.value} value={category.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {category.icon}
                                            <span>{category.label}</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Search Field */}
                        <TextField
                            size="small"
                            placeholder="Search files..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            autoComplete="off"
                            inputProps={{
                                autoComplete: 'off',
                                'aria-label': 'Search files'
                            }}
                            sx={{ 
                                width: 280,
                                bgcolor: 'background.paper',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            size="small" 
                                            onClick={handleClearSearch}
                                            edge="end"
                                            tabIndex={-1}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                </Box>

                {/* Active Filters Display */}
                {hasActiveFilters && !isSearching && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                            Filters:
                        </Typography>
                        {searchQuery.trim() && (
                            <Chip 
                                label={`Name: "${searchQuery}"`}
                                size="small"
                                onDelete={() => setSearchQuery('')}
                            />
                        )}
                        {selectedMimeCategory !== 'all' && (
                            <Chip 
                                icon={MIME_TYPE_CATEGORIES.find(c => c.value === selectedMimeCategory)?.icon}
                                label={MIME_TYPE_CATEGORIES.find(c => c.value === selectedMimeCategory)?.label}
                                size="small"
                                onDelete={() => setSelectedMimeCategory('all')}
                            />
                        )}
                    </Box>
                )}
            </Box>
            
            <Divider />

            {/* File List */}
            <DialogContent sx={{ p: 0, minHeight: '400px' }}>
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                        <CircularProgress />
                    </Box>
                ) : files.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6 }}>
                        <Typography color="text.secondary">
                            {hasActiveFilters 
                                ? 'No files match your search criteria.' 
                                : 'This folder is empty.'}
                        </Typography>
                        {hasActiveFilters && (
                            <Button 
                                onClick={handleClearFilters} 
                                sx={{ mt: 2 }}
                                startIcon={<ClearIcon />}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Box>
                ) : (
                    <List>
                        {files.map((file) => {
                            const isFolder = file.mimeType === FOLDER_MIME_TYPE;
                            const isSelected = selectedFiles.includes(file.id);

                            return (
                                <ListItem 
                                    key={file.id} 
                                    button
                                    divider
                                    onClick={() => isFolder ? handleFolderClick(file.id, file.name) : toggleSelection(file.id)}
                                    sx={{
                                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                                        '&:hover': {
                                            bgcolor: isSelected ? 'action.selected' : 'action.hover'
                                        }
                                    }}
                                    secondaryAction={
                                        !isFolder && (
                                            <Checkbox
                                                edge="end"
                                                checked={isSelected}
                                                onChange={() => toggleSelection(file.id)}
                                            />
                                        )
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: isFolder ? 'transparent' : 'primary.light' }}>
                                            {getFileIcon(file.mimeType)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText 
                                        primary={file.name} 
                                        secondary={
                                            <Box component="span" sx={{ display: 'flex', gap: 2 }}>
                                                <span>{isFolder ? 'Folder' : 'File'}</span>
                                                {file.size && <span>{formatFileSize(file.size)}</span>}
                                            </Box>
                                        }
                                        primaryTypographyProps={{ 
                                            fontWeight: isFolder ? 'bold' : 'normal',
                                            color: isSelected ? 'primary.main' : 'text.primary',
                                            noWrap: true,
                                            sx: { maxWidth: '70%' }
                                        }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            
            <Divider />
            
            {/* Footer */}
            <DialogActions sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ flexGrow: 1, ml: 1 }}>
                    {selectedFiles.length} file(s) selected
                </Typography>
                <Button onClick={handleClose} color="inherit">Cancel</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit} 
                    disabled={selectedFiles.length === 0 || loading}
                >
                    Select Files
                </Button>
            </DialogActions>
        </Dialog>
    );
}